'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { NumberField } from '@sola/ark-core/molecules/number-field/number-field.tsx';
import type { RadioItemProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { BadgeColors, FundOptionProps } from '@sola/schema/lib/schema.ts';
import { camelCase } from '@sola/utils/string.ts';
import type { FundQueryParams } from '../../../../utils/query-params.tsx';
import { useDonate } from '../donate-context.tsx';
import { DonateActionEnum, DonateFrequency } from '../donate.types.ts';
import { useDonateActions } from '../hooks/use-donate-actions.tsx';
import { useDonateValidation } from '../hooks/use-donate-validation.tsx';
export const DonateAmountSelector: React.FC<{
  className?: string;
  focusOnLoad?: boolean;
  alt?: boolean;
}> = t0 => {
  const $ = _c(58);
  const {
    className,
    focusOnLoad: t1,
    alt
  } = t0;
  const focusOnLoad = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["core", "donations", "skippy"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation(t2);
  const {
    config,
    state,
    props,
    variant,
    fundParams,
    refs,
    dispatch
  } = useDonate();
  useDonateValidation();
  const {
    handleAmountSelect,
    handleOtherAmountChange,
    handleInputFocus,
    handleInputBlur
  } = useDonateActions();
  let t3;
  if ($[1] !== config || $[2] !== fundParams || $[3] !== handleInputBlur || $[4] !== handleInputFocus || $[5] !== handleOtherAmountChange || $[6] !== props || $[7] !== refs || $[8] !== state.otherAmounts || $[9] !== t || $[10] !== variant) {
    t3 = frequency => {
      const freq = camelCase(frequency) as keyof FundQueryParams;
      const defaultFreq = `default${frequency}` as keyof FundQueryParams;
      const suggestedAmounts = (fundParams?.[freq] as number[])?.filter(v => v >= config.donationLimits.min && v <= config.donationLimits.max)?.map(v_0 => ({
        value: v_0,
        isDefault: v_0 === fundParams?.[defaultFreq]
      }) as FundOptionProps) ?? props.fund?.[`suggestedAmounts${frequency}`] ?? [];
      const items = [];
      if (suggestedAmounts.length > 1) {
        for (const amount of suggestedAmounts) {
          items.push({
            value: amount.value.toString(),
            text: t("numbers.currencyCustom", {
              amount: amount.value
            }),
            subText: variant === "grid" && amount.label ? amount.label : undefined,
            badge: amount.badge ? {
              label: amount.badge,
              color: "positive" as BadgeColors
            } as BadgeProps : undefined,
            type: "default"
          });
        }
      }
      items.push({
        value: "other",
        type: "other",
        text: t("donations:other"),
        element: <NumberField key={`${frequency}-other`} inputRef={refs[frequency]} name={frequency} aria-label={t("donations:other")} placeholder="0" onChange={e => handleOtherAmountChange(e.target.value)} value={state.otherAmounts[frequency]} onFocus={handleInputFocus} onBlur={handleInputBlur} prefix="$" />
      });
      return items;
    };
    $[1] = config;
    $[2] = fundParams;
    $[3] = handleInputBlur;
    $[4] = handleInputFocus;
    $[5] = handleOtherAmountChange;
    $[6] = props;
    $[7] = refs;
    $[8] = state.otherAmounts;
    $[9] = t;
    $[10] = variant;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const getRadioItemsForFrequency = t3;
  let t4;
  if ($[12] !== getRadioItemsForFrequency || $[13] !== state.donateFrequency) {
    t4 = getRadioItemsForFrequency(state.donateFrequency);
    $[12] = getRadioItemsForFrequency;
    $[13] = state.donateFrequency;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const items_0 = t4;
  if (!items_0.length) {
    return null;
  }
  let t5;
  if ($[15] !== fundParams || $[16] !== props || $[17] !== state.donateFrequency) {
    t5 = (fundParams?.[camelCase(state.donateFrequency) as keyof FundQueryParams] as number[])?.length || props.fund?.[`suggestedAmounts${state.donateFrequency}`]?.length || 0;
    $[15] = fundParams;
    $[16] = props;
    $[17] = state.donateFrequency;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const suggestedCount = t5;
  const suggestedAmountOnly = config.enabledFrequencies.length === 0 || suggestedCount < 2;
  const t6 = `DonateAmountSelector ${alt ? "flex-1" : ""} ${className || ""}`;
  let t7;
  if ($[19] !== alt || $[20] !== dispatch || $[21] !== focusOnLoad || $[22] !== handleInputBlur || $[23] !== handleInputFocus || $[24] !== state.donateFrequency || $[25] !== state.otherAmounts || $[26] !== t) {
    t7 = alt && <div className="Donate__other-input"><NumberField name="multiple-other" label={t("donations:amount")} placeholder="0" value={state.otherAmounts[state.donateFrequency]} onFocus={handleInputFocus} onBlur={handleInputBlur} onChange={e_0 => {
        dispatch({
          type: DonateActionEnum.SET_OTHER_AMOUNT,
          payload: {
            frequency: DonateFrequency.Monthly,
            amount: e_0.target.value
          }
        });
        dispatch({
          type: DonateActionEnum.SET_OTHER_AMOUNT,
          payload: {
            frequency: DonateFrequency.OneTime,
            amount: e_0.target.value
          }
        });
      }} prefix="$" isFocused={focusOnLoad} /></div>;
    $[19] = alt;
    $[20] = dispatch;
    $[21] = focusOnLoad;
    $[22] = handleInputBlur;
    $[23] = handleInputFocus;
    $[24] = state.donateFrequency;
    $[25] = state.otherAmounts;
    $[26] = t;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== alt || $[29] !== handleAmountSelect || $[30] !== handleOtherAmountChange || $[31] !== items_0 || $[32] !== state.donateFrequency || $[33] !== state.otherAmounts || $[34] !== state.otherSelected || $[35] !== state.selectedAmounts || $[36] !== suggestedAmountOnly || $[37] !== suggestedCount || $[38] !== t || $[39] !== variant) {
    t8 = !alt && !suggestedAmountOnly && <div className={cn(`Donate__items-${suggestedCount}`, {
      "Donate__other-input": state.otherSelected
    })}><RadioGroup key={`${state.donateFrequency}-amounts`} name={`${state.donateFrequency}-donateAmount`} aria-label={t("donations:chooseAmount")} items={items_0} variant={variant === "grid" ? "grid" : "buttons"} onChange={e_1 => {
        if (e_1.target.value === "other") {
          handleOtherAmountChange(state.otherAmounts[state.donateFrequency]);
        } else {
          handleAmountSelect(e_1.target.value);
        }
      }} value={state.selectedAmounts[state.donateFrequency].toString()} /></div>;
    $[28] = alt;
    $[29] = handleAmountSelect;
    $[30] = handleOtherAmountChange;
    $[31] = items_0;
    $[32] = state.donateFrequency;
    $[33] = state.otherAmounts;
    $[34] = state.otherSelected;
    $[35] = state.selectedAmounts;
    $[36] = suggestedAmountOnly;
    $[37] = suggestedCount;
    $[38] = t;
    $[39] = variant;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== alt || $[42] !== focusOnLoad || $[43] !== handleInputBlur || $[44] !== handleInputFocus || $[45] !== handleOtherAmountChange || $[46] !== refs || $[47] !== state.donateFrequency || $[48] !== state.otherAmounts || $[49] !== state.suggestedAmounts || $[50] !== suggestedAmountOnly || $[51] !== t) {
    t9 = !alt && suggestedAmountOnly && <div className="Donate__other-suggestedAmount"><p className="label-potent pb-snug-xl">{state.suggestedAmounts?.[state.donateFrequency] && t("donations:suggestedGift", {
          amount: state.suggestedAmounts[state.donateFrequency]
        })}</p><NumberField key={`${state.donateFrequency}-other`} inputRef={refs[state.donateFrequency]} name={state.donateFrequency} aria-label={t("donations:other")} placeholder="0" onChange={e_2 => handleOtherAmountChange(e_2.target.value)} value={state.otherAmounts[state.donateFrequency]} onFocus={handleInputFocus} onBlur={handleInputBlur} prefix="$" isFocused={focusOnLoad} /></div>;
    $[41] = alt;
    $[42] = focusOnLoad;
    $[43] = handleInputBlur;
    $[44] = handleInputFocus;
    $[45] = handleOtherAmountChange;
    $[46] = refs;
    $[47] = state.donateFrequency;
    $[48] = state.otherAmounts;
    $[49] = state.suggestedAmounts;
    $[50] = suggestedAmountOnly;
    $[51] = t;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  let t10;
  if ($[53] !== t6 || $[54] !== t7 || $[55] !== t8 || $[56] !== t9) {
    t10 = <div className={t6}>{t7}{t8}{t9}</div>;
    $[53] = t6;
    $[54] = t7;
    $[55] = t8;
    $[56] = t9;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  return t10;
};